import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CouponInput from './CouponInput';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  amount, 
  customerName,
  customerEmail,
  customerPhone,
  bookingDetails,
  orderDetails, 
}) => {
  // Initialize discountedAmount with the original amount
  const [discountedAmount, setDiscountedAmount] = useState(amount);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(false);

  // Add useEffect to update discountedAmount if amount changes
  useEffect(() => {
    // If no coupon has been applied, discountedAmount should always match amount
    if (!appliedCoupon) {
      setDiscountedAmount(amount);
    }
  }, [amount, appliedCoupon]);

  if (!isOpen) return null;

  const handleApplyCoupon = (discount) => {
    if (discount > 0) {
      setDiscountedAmount(amount - discount);
      setAppliedCoupon(true);
    } else {
      // If discount is 0 or invalid, reset to original amount
      setDiscountedAmount(amount);
      setAppliedCoupon(false);
    }
  };

  const handlePayment = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Make sure we're using the correct amount (original if no coupon applied)
      const finalAmount = appliedCoupon ? discountedAmount : amount;
      
      const paymentDetails = {
        ...(bookingDetails || orderDetails),
        customerName,
        customerEmail,
        customerPhone,
        amount: finalAmount,
        paymentType: bookingDetails ? 'booking' : 'order'
      };
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/payment/initialize`, {
        params: {
          amount: finalAmount * 100, // Convert to kobo for paystack
          email: customerEmail,
          metadata: {
            details: paymentDetails
          },
          callback_url: paymentDetails.paymentType === 'booking' 
            ? "/booking-confirmation-pg"
            : "/order-confirmation-pg"
        }
      });
  
      if (response.data.status) {
        localStorage.setItem('pendingDetails', JSON.stringify(paymentDetails));
        window.location.href = response.data.data.authorization_url;
      } else {
        setError('Payment initialization failed');
        localStorage.removeItem('pendingDetails');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment initialization failed. Please try again.');
      localStorage.removeItem('pendingDetails');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md mx-auto">
        <div className="p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Complete Payment</h3>
          
          <div className="mb-4 space-y-1 sm:space-y-2">
            <p className="text-sm sm:text-base">Name: {customerName}</p>
            <p className="text-sm sm:text-base break-all">Email: {customerEmail}</p>
            {bookingDetails && (
              <p className="text-sm sm:text-base">
                Service: {bookingDetails.subservices.join(', ')}
              </p>
            )}
          </div>

          {error && (
            <p className="text-red-500 mb-3 sm:mb-4 text-xs sm:text-sm">{error}</p>
          )}

          <p className="text-blue-500 text-xs sm:text-sm mb-2">
            Do you have a coupon Code?
          </p>

          <CouponInput 
            amount={amount} 
            onApplyCoupon={handleApplyCoupon}
          />

          <div className="mb-4 space-y-1 sm:space-y-2">
            <p className="text-base sm:text-lg font-bold">
              Original Amount: ₦{amount.toLocaleString()}
            </p>
            {appliedCoupon && (
              <p className="text-base sm:text-lg font-bold text-green-600">
                Discounted Amount: ₦{discountedAmount.toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={handlePayment}
              className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center text-sm sm:text-base"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg 
                    className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing...
                </>
              ) : (
                'Make Payment'
              )}
            </button>
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;